import { useLoggedInUser } from '@tcc/shared-data-lib';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getGraphQlClient } from '../graphql/graphqlClient';
import { canAccessCropPlanningQuery } from '../graphql/queries/canAccessCropPlanning';
import { useUserLoggedInConfigs } from '../utils/sharedData';
import { reportError } from '../utils/datadog';

interface CanAccessCropPlanningData {
  canAccessCropPlanning: boolean;
}

interface UseCanAccessCropPlanningResult {
  canAccess: boolean;
  isLoading: boolean;
}


// Keyed by userId to avoid leaking access state across accounts on shared machines
const CACHE_KEY_PREFIX = 'fv:canAccessCropPlanning';

function getCacheKey(userId: string): string {
  return `${CACHE_KEY_PREFIX}:${userId}`;
}

function getCachedAccess(userId: string): boolean | null {
  try {
    const raw = localStorage.getItem(getCacheKey(userId));
    if (!raw) return null;
    return JSON.parse(raw) === true;
  } catch {
    return null;
  }
}

function setCachedAccess(userId: string, value: boolean): void {
  try {
    localStorage.setItem(getCacheKey(userId), JSON.stringify(value));
  } catch {
    // ignore if localStorage full or unavailable
  }
}

/**
 * Deferred hook for crop planning nav access.
 *
 * Runs independently from the main nav loading pipeline so it doesn't
 * block other navigation items from rendering. Uses localStorage to show
 * the Plans tab instantly on return visits, with a background revalidation.
 */

// Skipping query check for BR/AR due to a known BE issue with permission checks in those countries.
// Currently not using Crop Planning features in those markets
// This is a temporary measure until the issue is resolved.
const RESTRICTED_COUNTRY_CODES = ['BR', 'AR'];

export const useCanAccessCropPlanning = (): UseCanAccessCropPlanningResult => {
  const { user } = useLoggedInUser(useUserLoggedInConfigs);
  const userId = user?.details?.uuid;
  const countryCode = user?.details?.countryCode;

  const [canAccess, setCanAccess] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const canAccessRef = useRef(canAccess);
  canAccessRef.current = canAccess;

  const isAllowedCountry = countryCode && !RESTRICTED_COUNTRY_CODES.includes(countryCode);

  const fetchAccess = useCallback(
    async (uid: string) => {
      try {
        // Serve cached value first for instant display on return visits
        const cached = getCachedAccess(uid);

        if (cached !== null) {
          setCanAccess(cached);
        }

        // Always revalidate against the server to pick up access changes
        const client = getGraphQlClient({ isUApi: true });
        const { data } = await client.query<CanAccessCropPlanningData>({
          query: canAccessCropPlanningQuery,
          variables: { userId: uid },
          fetchPolicy: 'network-only',
        });

        const result = !!data?.canAccessCropPlanning;
        setCachedAccess(uid, result);
        setCanAccess(result);
      } catch (error) {
        // On error, keep cached value if available; otherwise default to false
        reportError('FieldViewCropPlanningAccessError', error, { hook: 'useCanAccessCropPlanning', userId: uid });
        if (canAccessRef.current === null) {
          setCanAccess(getCachedAccess(uid) ?? false);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!userId || !isAllowedCountry) {
      setCanAccess(false);
      setIsLoading(false);
      return;
    }

    fetchAccess(userId);
  }, [userId, fetchAccess, isAllowedCountry]);

  return {
    canAccess: canAccess ?? false,
    isLoading,
  };
};
